import { AsphaltCutter } from "../../../interfaces/api/AsphaltCutter";
import { Bucket } from "../../../interfaces/api/Bucket";
import { Compactor } from "../../../interfaces/api/Compactor";
import { GradingBeam } from "../../../interfaces/api/GradingBeam";
import { Grapple } from "../../../interfaces/api/Grapple";
import { GrappleModule } from "../../../interfaces/api/GrappleModule";
import { LowerBracket } from "../../../interfaces/api/LowerBracket";
import { RailGrapple } from "../../../interfaces/api/RailGrapple";
import { Ripper } from "../../../interfaces/api/Ripper";
import { Rototilt } from "../../../interfaces/api/Rototilt";
import { ProductType } from "../../../shared/enums/ProductType";
import { filterHelper } from "../../../shared/helpers/filterHelper";
import { filtering } from "../../../shared/helpers/productFilterHelper";
import {
  ProductItem,
  ProductItemWithTypeName,
} from "../../../shared/types/ProductItem";
import { useMachineTonnage } from "../../../store/hooks/useMachineTonnage";
import { useMarket } from "../../../store/hooks/useMarket";
import { ProductData } from "../../../store/interfaces/productData";
import { ToolsProductType, useProductTypes } from "./useProductTypes";

interface FilterProductItemsProps {
  productItemsData: ProductData;
  productType: ProductType;
  selectedLowerBracket: LowerBracket;
  selectedTiltrotator: Rototilt;
}

interface FilterProductItemsOutput {
  filteredProductItems: ProductItem[];
}

const getProductTypeName = (
  productType: ProductType,
  toolsProductTypes: ToolsProductType[]
): string => {
  const index = toolsProductTypes.findIndex(
    (toolsProductType) => toolsProductType.productType === productType
  );

  if (index > -1) {
    return toolsProductTypes[index].name;
  }

  return "Unknown product type";
};

const castToProductItemWithTypeNameArray = (
  productItems: ProductItem[],
  productType: ProductType,
  toolsProductTypes: ToolsProductType[]
): ProductItemWithTypeName[] => {
  const productTypeName = getProductTypeName(productType, toolsProductTypes);

  const productItemsWithTypeName = productItems.map((productItem) => {
    return { ...productItem, productTypeName };
  });

  return productItemsWithTypeName;
};

const castToTypedArray = (
  productItemsData: ProductData,
  productType: ProductType
): ProductItem[] => {
  switch (productType) {
    case ProductType.AsphaltCutters:
      return productItemsData.data as AsphaltCutter[];

    case ProductType.Compactors:
      return productItemsData.data as Compactor[];

    case ProductType.GradingBeams:
      return productItemsData.data as GradingBeam[];

    case ProductType.Grapples:
      return productItemsData.data as Grapple[];

    case ProductType.GrappleModules:
      return productItemsData.data as GrappleModule[];

    case ProductType.RailGrapples:
      return productItemsData.data as RailGrapple[];

    case ProductType.Rippers:
      return productItemsData.data as Ripper[];

    case ProductType.Buckets:
      return productItemsData.data as Bucket[];

    default:
      return [];
  }
};

export const useFilterProductItems = ({
  productItemsData,
  productType,
  selectedLowerBracket,
  selectedTiltrotator,
}: FilterProductItemsProps): FilterProductItemsOutput => {
  const machineTonnage = useMachineTonnage();
  const market = useMarket();
  const { toolsProductTypes } = useProductTypes();

  if (!productItemsData) {
    return {
      filteredProductItems: [],
    };
  }

  const unFilteredProductItems = castToTypedArray(
    productItemsData,
    productType
  );

  const filteredProductItems = unFilteredProductItems
    .filter((productItem) => filtering.market(productItem, market))
    .filter((productItem) =>
      filterHelper.toolsProductItems({
        machineTonnage,
        market,
        productItem: productItem,
        selectedLowerBracket,
        selectedTiltrotator,
      })
    );

  return {
    filteredProductItems: castToProductItemWithTypeNameArray(
      filteredProductItems,
      productType,
      toolsProductTypes
    ),
  };
};
